<script setup>
/* global axios */
// Aba "Templates" das configurações do inbox de WhatsApp oficial (Cloud API).
// Lista/sincroniza (Meta) e cria/exclui templates HSM do canal.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const route = useRoute();
const accountId = route.params.accountId;
const base = `/api/v1/accounts/${accountId}/kanban/whatsapp_templates`;
const inboxId = computed(() => props.inbox.id);

const templates = ref([]);
const loading = ref(true);
const syncing = ref(false);
const search = ref('');
const statusFilter = ref('');
const categoryFilter = ref('');

const showCreate = ref(false);
const saving = ref(false);
const error = ref('');
const freshForm = () => ({
  name: '',
  category: 'MARKETING',
  language: 'pt_BR',
  header_format: 'none',
  header_text: '',
  header_handle: '',
  header_media_name: '',
  header_media_preview: '',
  body: '',
  footer: '',
  buttons: [],
});
const form = ref(freshForm());
// exemplos por índice de variável ({{1}} → "João"), exigidos pela Meta
const bodyVarExamples = ref({});
const headerVarExamples = ref({});

// índices das variáveis numeradas {{1}},{{2}}… em ordem
const extractVarIndices = text => {
  const matches = `${text || ''}`.match(/\{\{(\d+)\}\}/g) || [];
  return [...new Set(matches.map(m => parseInt(m.replace(/\{\{|\}\}/g, ''), 10)))].sort(
    (a, b) => a - b
  );
};
// variáveis com nome (ex: {{nome}}) que a Meta NÃO aceita
const extractNamedVars = text => {
  const matches = `${text || ''}`.match(/\{\{([^}]+)\}\}/g) || [];
  return [...new Set(matches.filter(m => !/^\{\{\d+\}\}$/.test(m)))];
};

const bodyVarIndices = computed(() => extractVarIndices(form.value.body));
const headerVarIndices = computed(() =>
  form.value.header_format === 'TEXT'
    ? extractVarIndices(form.value.header_text)
    : []
);
const namedVarsBody = computed(() => extractNamedVars(form.value.body));

// monta a tag "{{n}}" em JS — não dá pra escrever literal no template (o Vue
// interpreta o }} interno como fim da interpolação).
const varTag = idx => `{{${idx}}}`;
const insertBodyVar = () => {
  const next = (bodyVarIndices.value[bodyVarIndices.value.length - 1] || 0) + 1;
  form.value.body = `${form.value.body || ''}{{${next}}}`;
};

// substitui {{n}} pelos exemplos (ou mantém {{n}}) para o preview
const fillVars = (text, examples) =>
  `${text || ''}`.replace(
    /\{\{(\d+)\}\}/g,
    (m, n) => examples[n] || examples[Number(n)] || m
  );
const previewBody = computed(() => fillVars(form.value.body, bodyVarExamples.value));
const previewHeader = computed(() =>
  fillVars(form.value.header_text, headerVarExamples.value)
);

const LANGUAGES = ['pt_BR', 'en_US', 'es_ES', 'en', 'es'];
const CATEGORIES = ['MARKETING', 'UTILITY', 'AUTHENTICATION'];
const STATUSES = ['APPROVED', 'PENDING', 'REJECTED', 'PAUSED', 'DISABLED'];
const HEADER_FORMATS = ['none', 'TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT'];
const MEDIA_HEADER_FORMATS = ['IMAGE', 'VIDEO', 'DOCUMENT'];
const isMediaHeader = computed(() =>
  MEDIA_HEADER_FORMATS.includes(form.value.header_format)
);
const ACCEPT_BY_FORMAT = {
  IMAGE: 'image/jpeg,image/png',
  VIDEO: 'video/mp4,video/3gpp',
  DOCUMENT: 'application/pdf',
};
const uploadingMedia = ref(false);
const mediaError = ref('');
const uploadHeaderMedia = async event => {
  const file = event.target.files?.[0];
  if (!file) return;
  uploadingMedia.value = true;
  mediaError.value = '';
  form.value.header_handle = '';
  form.value.header_media_name = file.name;
  form.value.header_media_preview =
    form.value.header_format === 'IMAGE' ? URL.createObjectURL(file) : '';
  try {
    const fd = new FormData();
    fd.append('inbox_id', inboxId.value);
    fd.append('file', file);
    const { data } = await axios.post(`${base}/upload_header_media`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    form.value.header_handle = data.handle;
  } catch (e) {
    mediaError.value =
      e?.response?.data?.error ||
      t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.MEDIA_UPLOAD_ERROR');
    form.value.header_media_name = '';
    form.value.header_media_preview = '';
  } finally {
    uploadingMedia.value = false;
  }
};
const BUTTON_TYPES = ['QUICK_REPLY', 'URL', 'PHONE_NUMBER'];

const STATUS_CLASS = {
  APPROVED: 'bg-n-teal-3 text-n-teal-11',
  PENDING: 'bg-n-amber-3 text-n-amber-11',
  REJECTED: 'bg-n-ruby-3 text-n-ruby-11',
  PAUSED: 'bg-n-alpha-2 text-n-slate-11',
  DISABLED: 'bg-n-alpha-2 text-n-slate-11',
};
const CATEGORY_CLASS = {
  MARKETING: 'bg-n-blue-3 text-n-blue-11',
  UTILITY: 'bg-n-iris-3 text-n-iris-11',
  AUTHENTICATION: 'bg-n-amber-3 text-n-amber-11',
};

const comp = (tpl, type) => (tpl.components || []).find(c => c.type === type);
const bodyText = tpl => comp(tpl, 'BODY')?.text || '';
const headerOf = tpl => comp(tpl, 'HEADER');
const footerText = tpl => comp(tpl, 'FOOTER')?.text || '';
const buttonsOf = tpl => comp(tpl, 'BUTTONS')?.buttons || [];

const filtered = computed(() =>
  templates.value.filter(
    tpl =>
      (!search.value ||
        (tpl.name || '').toLowerCase().includes(search.value.toLowerCase())) &&
      (!statusFilter.value || tpl.status === statusFilter.value) &&
      (!categoryFilter.value || tpl.category === categoryFilter.value)
  )
);

const loadTemplates = async () => {
  loading.value = true;
  try {
    templates.value =
      (await axios.get(`${base}?inbox_id=${inboxId.value}`)).data.data || [];
  } catch {
    templates.value = [];
  } finally {
    loading.value = false;
  }
};
const sync = async () => {
  syncing.value = true;
  try {
    templates.value =
      (await axios.post(`${base}/sync`, { inbox_id: inboxId.value })).data
        .data || [];
  } catch (e) {
    window.alert(
      e?.response?.data?.error || t('INBOX_MGMT.WHATSAPP_TEMPLATES.SYNC_ERROR')
    );
  } finally {
    syncing.value = false;
  }
};

const addButton = () => {
  if (form.value.buttons.length >= 3) return;
  form.value.buttons.push({
    type: 'QUICK_REPLY',
    text: '',
    url: '',
    phone_number: '',
  });
};
const removeButton = i => form.value.buttons.splice(i, 1);

const createTemplate = async () => {
  if (!form.value.name.trim() || !form.value.body.trim()) {
    error.value = t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.REQUIRED');
    return;
  }
  if (isMediaHeader.value && !form.value.header_handle) {
    error.value = t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.MEDIA_REQUIRED');
    return;
  }
  saving.value = true;
  error.value = '';
  try {
    await axios.post(base, {
      inbox_id: inboxId.value,
      ...form.value,
      body_examples: bodyVarExamples.value,
      header_examples: headerVarExamples.value,
    });
    showCreate.value = false;
    form.value = freshForm();
    bodyVarExamples.value = {};
    headerVarExamples.value = {};
    await loadTemplates();
  } catch (e) {
    error.value =
      e?.response?.data?.error || t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.ERROR');
  } finally {
    saving.value = false;
  }
};

const deleteTemplate = async tpl => {
  if (
    !window.confirm(
      t('INBOX_MGMT.WHATSAPP_TEMPLATES.CONFIRM_DELETE', { name: tpl.name })
    )
  )
    return;
  await axios.delete(`${base}/${encodeURIComponent(tpl.name)}`, {
    data: { inbox_id: inboxId.value },
    params: { inbox_id: inboxId.value },
  });
  await loadTemplates();
};

const previewName = key => `INBOX_MGMT.WHATSAPP_TEMPLATES.STATUS.${key}`;

onMounted(loadTemplates);
</script>

<template>
  <div class="w-full">
    <!-- toolbar -->
    <div class="flex flex-wrap items-center gap-2 mb-4">
      <input
        v-model="search"
        class="w-56 px-3 !h-9 !mb-0 text-sm border-0 rounded-lg outline outline-1 outline-offset-[-1px] bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 outline-n-weak focus:outline-n-brand focus:ring-0"
        :placeholder="t('INBOX_MGMT.WHATSAPP_TEMPLATES.SEARCH')"
      />
      <select
        v-model="statusFilter"
        class="!w-auto pl-3 pr-9 !h-9 !mb-0 text-sm border-0 rounded-lg outline outline-1 outline-offset-[-1px] bg-n-alpha-black2 text-n-slate-12 outline-n-weak focus:outline-n-brand focus:ring-0"
      >
        <option value="">
          {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.ALL_STATUSES') }}
        </option>
        <option v-for="s in STATUSES" :key="s" :value="s">
          {{ t(previewName(s)) }}
        </option>
      </select>
      <select
        v-model="categoryFilter"
        class="!w-auto pl-3 pr-9 !h-9 !mb-0 text-sm border-0 rounded-lg outline outline-1 outline-offset-[-1px] bg-n-alpha-black2 text-n-slate-12 outline-n-weak focus:outline-n-brand focus:ring-0"
      >
        <option value="">
          {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.ALL_CATEGORIES') }}
        </option>
        <option v-for="c in CATEGORIES" :key="c" :value="c">
          {{ t(`INBOX_MGMT.WHATSAPP_TEMPLATES.CATEGORY.${c}`) }}
        </option>
      </select>
      <div class="flex gap-2 ml-auto">
        <NextButton
          :label="
            syncing
              ? t('INBOX_MGMT.WHATSAPP_TEMPLATES.SYNCING')
              : t('INBOX_MGMT.WHATSAPP_TEMPLATES.SYNC')
          "
          icon="i-lucide-refresh-cw"
          slate
          faded
          sm
          :disabled="syncing"
          @click="sync"
        />
        <NextButton
          :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.NEW')"
          icon="i-lucide-plus"
          blue
          sm
          @click="showCreate = true"
        />
      </div>
    </div>

    <p v-if="loading" class="py-8 text-sm text-center text-n-slate-10">
      {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.LOADING') }}
    </p>
    <p
      v-else-if="!filtered.length"
      class="py-8 text-sm text-center text-n-slate-10"
    >
      {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.EMPTY') }}
    </p>

    <div v-else class="grid grid-cols-1 gap-3 md:grid-cols-2">
      <div
        v-for="tpl in filtered"
        :key="tpl.id || tpl.name"
        class="flex flex-col p-4 border rounded-xl border-n-weak bg-n-solid-1"
      >
        <div class="flex items-start justify-between gap-2">
          <span class="font-medium break-all text-n-slate-12">{{
            tpl.name
          }}</span>
          <button
            class="p-1 rounded-md text-n-ruby-11 hover:bg-n-ruby-3 shrink-0"
            :title="t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE')"
            @click="deleteTemplate(tpl)"
          >
            <svg
              class="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
        <div class="flex flex-wrap gap-1.5 mt-2">
          <span
            class="px-2 py-0.5 text-[0.6875rem] font-medium rounded-full"
            :class="STATUS_CLASS[tpl.status] || 'bg-n-alpha-2 text-n-slate-11'"
            >{{ t(previewName(tpl.status)) }}</span
          >
          <span
            class="px-2 py-0.5 text-[0.6875rem] font-medium rounded-full"
            :class="
              CATEGORY_CLASS[tpl.category] || 'bg-n-alpha-2 text-n-slate-11'
            "
            >{{
              t(`INBOX_MGMT.WHATSAPP_TEMPLATES.CATEGORY.${tpl.category}`)
            }}</span
          >
          <span
            class="px-2 py-0.5 text-[0.6875rem] font-medium rounded-full bg-n-alpha-2 text-n-slate-11"
            >{{ tpl.language }}</span
          >
        </div>
        <div
          class="p-3 mt-3 text-sm border rounded-lg bg-n-alpha-1 border-n-weak text-n-slate-12"
        >
          <p
            v-if="headerOf(tpl) && headerOf(tpl).text"
            class="font-semibold break-words whitespace-pre-wrap"
          >
            {{ headerOf(tpl).text }}
          </p>
          <p
            v-else-if="headerOf(tpl)"
            class="text-xs font-medium text-n-slate-10"
          >
            [{{ headerOf(tpl).format }}]
          </p>
          <p class="break-words whitespace-pre-wrap text-n-slate-11">
            {{ bodyText(tpl) }}
          </p>
          <p
            v-if="footerText(tpl)"
            class="mt-1 text-xs break-words whitespace-pre-wrap text-n-slate-10"
          >
            {{ footerText(tpl) }}
          </p>
          <div
            v-if="buttonsOf(tpl).length"
            class="pt-2 mt-2 border-t border-n-weak"
          >
            <div
              v-for="(btn, bi) in buttonsOf(tpl)"
              :key="bi"
              class="py-1 text-xs font-medium text-center text-n-blue-11"
            >
              {{ btn.text }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- modal criar -->
    <div
      v-if="showCreate"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-alpha-black2"
      @click.self="showCreate = false"
    >
      <div
        class="w-[48rem] max-w-full bg-n-background rounded-xl shadow-xl max-h-[92vh] overflow-y-auto"
      >
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
        >
          <h2 class="text-heading-2 text-n-slate-12">
            {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.NEW') }}
          </h2>
          <button
            class="text-n-slate-10 hover:text-n-slate-12"
            @click="showCreate = false"
          >
            <svg
              class="w-5 h-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path stroke-linecap="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div class="grid grid-cols-1 gap-4 p-6 md:grid-cols-2">
          <!-- form -->
          <div class="space-y-3">
            <p v-if="error" class="text-sm text-n-ruby-11">{{ error }}</p>
            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME')
              }}</label>
              <input
                v-model="form.name"
                class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
            </div>
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                  t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CATEGORY')
                }}</label>
                <select
                  v-model="form.category"
                  class="w-full pl-3 pr-9 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
                >
                  <option v-for="c in CATEGORIES" :key="c" :value="c">
                    {{ t(`INBOX_MGMT.WHATSAPP_TEMPLATES.CATEGORY.${c}`) }}
                  </option>
                </select>
              </div>
              <div>
                <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                  t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.LANGUAGE')
                }}</label>
                <select
                  v-model="form.language"
                  class="w-full pl-3 pr-9 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
                >
                  <option v-for="l in LANGUAGES" :key="l" :value="l">
                    {{ l }}
                  </option>
                </select>
              </div>
            </div>
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                  t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER')
                }}</label>
                <select
                  v-model="form.header_format"
                  class="w-full pl-3 pr-9 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
                >
                  <option v-for="h in HEADER_FORMATS" :key="h" :value="h">
                    {{ t(`INBOX_MGMT.WHATSAPP_TEMPLATES.HEADER.${h}`) }}
                  </option>
                </select>
              </div>
              <div v-if="form.header_format === 'TEXT'">
                <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                  t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_TEXT')
                }}</label>
                <input
                  v-model="form.header_text"
                  class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
                />
              </div>
              <!-- Cabeçalho de MÍDIA: upload de arquivo (não link) -->
              <div v-if="isMediaHeader">
                <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                  t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_MEDIA')
                }}</label>
                <label
                  class="flex items-center gap-2 px-3 py-2 text-sm border border-dashed rounded-lg cursor-pointer border-n-weak text-n-brand hover:bg-n-brand/5 w-fit"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    class="w-4 h-4"
                  >
                    <path
                      stroke-linecap="round"
                      d="M12 16V4m0 0L8 8m4-4l4 4M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2"
                    />
                  </svg>
                  {{
                    uploadingMedia
                      ? t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.MEDIA_UPLOADING')
                      : t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.MEDIA_UPLOAD')
                  }}
                  <input
                    type="file"
                    class="hidden"
                    :accept="ACCEPT_BY_FORMAT[form.header_format]"
                    :disabled="uploadingMedia"
                    @change="uploadHeaderMedia"
                  />
                </label>
                <div
                  v-if="form.header_media_preview"
                  class="mt-2"
                >
                  <img
                    :src="form.header_media_preview"
                    class="object-cover w-24 h-24 rounded-lg border border-n-weak"
                  />
                </div>
                <p
                  v-if="form.header_handle"
                  class="mt-1 text-xs text-n-teal-11 flex items-center gap-1"
                >
                  ✓ {{ form.header_media_name }}
                </p>
                <p v-if="mediaError" class="mt-1 text-xs text-n-ruby-11">
                  {{ mediaError }}
                </p>
                <p class="mt-1 text-[0.6875rem] text-n-slate-10">
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.MEDIA_HINT') }}
                </p>
              </div>
            </div>
            <div>
              <div class="flex items-center justify-between mb-1">
                <label class="text-sm font-medium text-n-slate-12">{{
                  t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY')
                }}</label>
                <button
                  type="button"
                  class="text-xs font-medium text-n-brand hover:underline"
                  @click="insertBodyVar"
                >
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.INSERT_VAR') }}
                </button>
              </div>
              <textarea
                v-model="form.body"
                rows="4"
                maxlength="1024"
                :placeholder="
                  t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY_PLACEHOLDER')
                "
                class="w-full px-3 py-2 text-sm border rounded-lg resize-none border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
              <p class="mt-1 text-xs text-n-slate-10">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY_HINT') }}
              </p>

              <!-- aviso: variáveis nomeadas não suportadas -->
              <div
                v-if="namedVarsBody.length"
                class="p-2 mt-2 text-xs border rounded-lg border-n-amber-6 bg-n-amber-2 text-n-amber-11"
              >
                <span class="font-medium">{{
                  t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAMED_VARS_WARNING')
                }}</span>
                {{ namedVarsBody.join(', ') }} —
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAMED_VARS_HINT') }}
              </div>

              <!-- exemplos das variáveis do corpo -->
              <div
                v-if="bodyVarIndices.length"
                class="p-3 mt-2 space-y-2 border rounded-lg border-n-weak bg-n-alpha-1"
              >
                <p class="text-xs font-medium text-n-slate-11">
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.VARS_TITLE') }}
                </p>
                <p class="text-xs text-n-slate-10">
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.VARS_HINT') }}
                </p>
                <div
                  v-for="idx in bodyVarIndices"
                  :key="`b${idx}`"
                  class="flex items-center gap-2"
                >
                  <span
                    class="w-10 font-mono text-xs text-center rounded text-n-brand bg-n-alpha-2 py-1"
                    >{{ varTag(idx) }}</span
                  >
                  <input
                    v-model="bodyVarExamples[idx]"
                    :placeholder="
                      t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.VAR_EXAMPLE_PLACEHOLDER', {
                        var: varTag(idx),
                      })
                    "
                    class="flex-1 px-3 py-1.5 text-sm border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                  />
                </div>
              </div>

              <!-- exemplos das variáveis do cabeçalho -->
              <div
                v-if="headerVarIndices.length"
                class="p-3 mt-2 space-y-2 border rounded-lg border-n-weak bg-n-alpha-1"
              >
                <p class="text-xs font-medium text-n-slate-11">
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_VARS_TITLE') }}
                </p>
                <div
                  v-for="idx in headerVarIndices"
                  :key="`h${idx}`"
                  class="flex items-center gap-2"
                >
                  <span
                    class="w-10 font-mono text-xs text-center rounded text-n-brand bg-n-alpha-2 py-1"
                    >{{ varTag(idx) }}</span
                  >
                  <input
                    v-model="headerVarExamples[idx]"
                    :placeholder="
                      t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.VAR_EXAMPLE_PLACEHOLDER', {
                        var: varTag(idx),
                      })
                    "
                    class="flex-1 px-3 py-1.5 text-sm border rounded-lg border-n-weak bg-n-background text-n-slate-12"
                  />
                </div>
              </div>
            </div>
            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FOOTER')
              }}</label>
              <input
                v-model="form.footer"
                class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
            </div>
            <div>
              <div class="flex items-center justify-between mb-1">
                <label class="text-xs text-n-slate-10">{{
                  t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS')
                }}</label>
                <button
                  v-if="form.buttons.length < 3"
                  class="text-xs font-medium text-n-brand hover:underline"
                  @click="addButton"
                >
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.ADD_BUTTON') }}
                </button>
              </div>
              <div
                v-for="(btn, i) in form.buttons"
                :key="i"
                class="flex flex-wrap items-center gap-2 mb-2"
              >
                <select
                  v-model="btn.type"
                  class="pl-2 pr-8 py-1.5 text-xs border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
                >
                  <option v-for="bt in BUTTON_TYPES" :key="bt" :value="bt">
                    {{ t(`INBOX_MGMT.WHATSAPP_TEMPLATES.BUTTON.${bt}`) }}
                  </option>
                </select>
                <input
                  v-model="btn.text"
                  :placeholder="
                    t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BTN_TEXT')
                  "
                  class="flex-1 px-2 py-1.5 text-xs border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
                />
                <input
                  v-if="btn.type === 'URL'"
                  v-model="btn.url"
                  :placeholder="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BTN_URL')"
                  class="flex-1 px-2 py-1.5 text-xs border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
                />
                <input
                  v-if="btn.type === 'PHONE_NUMBER'"
                  v-model="btn.phone_number"
                  :placeholder="
                    t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BTN_PHONE')
                  "
                  class="flex-1 px-2 py-1.5 text-xs border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
                />
                <button
                  class="p-1 rounded text-n-ruby-11 hover:bg-n-ruby-3"
                  @click="removeButton(i)"
                >
                  <svg
                    class="w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path stroke-linecap="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- preview estilo WhatsApp -->
          <div class="md:sticky md:top-4 self-start">
            <p class="mb-1 text-xs font-medium text-n-slate-11">
              {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW') }}
            </p>
            <p class="mb-3 text-xs text-n-slate-10">
              {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW_SUBTITLE') }}
            </p>
            <div
              class="p-4 rounded-xl bg-[#e5ddd5] dark:bg-[#0b1014]"
              style="
                background-image: url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2240%22 height=%2240%22><circle cx=%221%22 cy=%221%22 r=%221%22 fill=%22%23000%22 opacity=%220.04%22/></svg>');
              "
            >
              <div
                v-if="
                  form.body ||
                  form.footer ||
                  (form.header_format === 'TEXT' && form.header_text) ||
                  form.buttons.length
                "
                class="overflow-hidden bg-white shadow-sm dark:bg-n-solid-2 rounded-lg max-w-[18rem]"
              >
                <div class="px-3 pt-2.5 pb-2">
                  <p
                    v-if="form.header_format === 'TEXT' && previewHeader"
                    class="text-sm font-bold break-words whitespace-pre-wrap text-n-slate-12"
                  >
                    {{ previewHeader }}
                  </p>
                  <p
                    v-if="previewBody"
                    class="mt-0.5 text-sm break-words whitespace-pre-wrap text-n-slate-12"
                  >
                    {{ previewBody }}
                  </p>
                  <p
                    v-if="form.footer"
                    class="mt-1 text-xs break-words whitespace-pre-wrap text-n-slate-10"
                  >
                    {{ form.footer }}
                  </p>
                  <p class="mt-1 text-[0.625rem] text-right text-n-slate-9">
                    12:00
                  </p>
                </div>
                <div
                  v-if="form.buttons.some(b => b.text)"
                  class="border-t border-n-weak"
                >
                  <div
                    v-for="(btn, bi) in form.buttons.filter(b => b.text)"
                    :key="bi"
                    class="py-2 text-xs font-medium text-center border-b text-n-blue-11 border-n-weak last:border-0"
                  >
                    <span v-if="btn.type === 'URL'">🔗 </span>
                    <span v-else-if="btn.type === 'PHONE_NUMBER'">📞 </span>
                    {{ btn.text }}
                  </div>
                </div>
              </div>
              <p
                v-else
                class="py-10 text-xs text-center text-n-slate-11 dark:text-n-slate-10"
              >
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.PREVIEW_EMPTY') }}
              </p>
            </div>
          </div>
        </div>
        <div class="flex justify-end gap-2 px-6 py-4 border-t border-n-weak">
          <NextButton
            :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.CANCEL')"
            slate
            faded
            sm
            @click="showCreate = false"
          />
          <NextButton
            :label="
              saving
                ? t('INBOX_MGMT.WHATSAPP_TEMPLATES.SAVING')
                : t('INBOX_MGMT.WHATSAPP_TEMPLATES.SUBMIT')
            "
            blue
            sm
            :disabled="saving"
            @click="createTemplate"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Neutraliza o estilo global de form (_base.scss) que adiciona mb-4 e padding
   próprios aos input/select/textarea, desalinhando os controles desta aba. */
input,
select,
textarea {
  margin-bottom: 0 !important;
}
</style>
